/**
 * useFirestoreCollection Hook
 *
 * Real-time Firestore collection listener with automatic cleanup.
 * Provides live updates when Firestore data changes.
 *
 * Uses the {@link FirestoreClient} abstraction so unit tests can run
 * against the in-memory fake without touching the Firebase SDK.
 *
 * @example
 * ```typescript
 * const { data, loading, error } = useFirestoreCollection<Plan>({
 *   collectionPath: 'plans',
 *   constraints: [{ type: 'where', field: 'active', operator: '==', value: true }],
 *   transform: (doc) => doc.data() as Plan,
 * });
 * ```
 */

import { useEffect, useState } from "react";
import {
  getFirestoreClient,
  type FirestoreConstraint,
  type FirestoreDocSnapshot,
} from "../lib/firestoreClient";
import { logger } from "../utils/logger";

interface UseFirestoreCollectionOptions<T> {
  /** Firestore collection path (e.g., 'plans', 'users/uid/scenarios') */
  collectionPath: string;

  /** Optional query constraints (where, orderBy). */
  constraints?: FirestoreConstraint[];

  /** Transform function to convert a FirestoreDocSnapshot to typed data */
  transform: (doc: FirestoreDocSnapshot) => T;

  /** Optional: Disable the listener (useful for conditional fetching) */
  enabled?: boolean;
}

interface UseFirestoreCollectionResult<T> {
  /** Array of documents from the collection */
  data: T[];

  /** Loading state - true during initial fetch */
  loading: boolean;

  /** Error state - null if no error occurred */
  error: Error | null;

  /** Whether the listener is active */
  listening: boolean;
}

/**
 * Hook to listen to Firestore collection changes in real-time
 *
 * Automatically subscribes on mount and unsubscribes on unmount.
 * Updates component state whenever Firestore data changes.
 */
export function useFirestoreCollection<T>({
  collectionPath,
  constraints = [],
  transform,
  enabled = true,
}: UseFirestoreCollectionOptions<T>): UseFirestoreCollectionResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<Error | null>(null);
  const [listening, setListening] = useState(enabled);

  useEffect(() => {
    if (!enabled || !collectionPath) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setListening(true);

    const client = getFirestoreClient();
    // Split "users/uid/scenarios" → ["users", "uid", "scenarios"]
    const pathSegments = collectionPath.split("/").filter(Boolean);

    const unsubscribe = client.onCollectionSnapshot(
      pathSegments,
      constraints,
      (result) => {
        try {
          const docs = result.docs.map((doc) => transform(doc));
          setData(docs);
          setError(null);
          setLoading(false);

          logger.debug("Firestore collection loaded", {
            collection: collectionPath,
            count: docs.length,
          });
        } catch (err) {
          logger.error("Failed to transform Firestore documents", {
            collection: collectionPath,
            error: err,
          });
          setError(err as Error);
          setLoading(false);
        }
      },
      (err) => {
        logger.error("Firestore collection listener error", {
          collection: collectionPath,
          error: err,
        });
        setError(err);
        setLoading(false);
        setListening(false);
      }
    );

    // Cleanup: unsubscribe from listener on unmount or when dependencies change
    return () => {
      unsubscribe();
      setListening(false);
    };
    // NOTE: 'transform' and 'constraints' are intentionally omitted from dependencies.
    // transform is a pure function and constraints should be stable references.
    // Only collectionPath and enabled should trigger re-subscriptions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionPath, enabled]);

  return { data, loading, error, listening };
}
